import React, { useEffect, useState } from "react";

import PanelTemplate from "../../templates/Panel";
import SortableTable from "../../components/SortableTable";
import Box from "../../components/Box";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import { renderError } from "../../helpers/errors";
import moment from "moment";
import PageHeader from "../../components/PageHeader";
import Button from "../../components/Button";
import Modal from "../../components/Modal";
import Input from "../../components/Input";
import InputFile from "../../components/InputFile";
import InputSelect from "../../components/InputSelect";
import { toast } from "react-toastify";
import BoxTabs from "../../components/BoxTabs";
import { ModalDelete } from "../../components/ModalDelete";

const statusOptions = [
    { label: 'Ativo', value: true },
    { label: 'Inativo', value: false }
];

/**
 * Rótulo da aba/opção a partir do formato que a API entrega (BDM-1136):
 * "Slider (2.000px por 600px)".
 */
const formatSize = (width, height) => `${width.toLocaleString('pt-BR')}px por ${height.toLocaleString('pt-BR')}px`;

const formatLabel = (format) => `${format.label} (${formatSize(format.width, format.height)})`;

// Formatos largos trazem a medida de celular (BDM-1190); os demais já
// funcionam no celular com a mesma imagem.
const toTypeOptions = (formats) => (formats ?? []).map(format => ({
    label: formatLabel(format),
    aba: format.label,
    value: format.type,
    mobileSize: format.mobile_width && format.mobile_height
        ? formatSize(format.mobile_width, format.mobile_height)
        : null
}));

const targetOptions = [
    { label: 'Não se aplica', value: null },
    { label: 'Nova aba', value: '_blank' },
    { label: 'Mesma aba', value: '_self' }
];

const BannerList = () => {
    const navigate = useNavigate();

    const [typeOptions, setTypeOptions] = useState([]);

    const [loading, setLoading] = useState(true);
    const [banners, setBanners] = useState([]);
    const [data, setData] = useState({header: [], rows: []});

    const [showCreate, setShowCreate] = useState(false);
    const [showEdit, setShowEdit] = useState(false);

    const [id, setId] = useState('');
    const [name, setName] = useState('');
    const [status, setStatus] = useState('');
    const [position, setPosition] = useState('');
    const [type, setType] = useState('');
    const [link, setLink] = useState('');
    const [target, setTarget] = useState('');

    const [imageBase64, setImageBase64] = useState('');
    const [imageName, setImageName] = useState('');

    const [mobileImageBase64, setMobileImageBase64] = useState('');
    const [mobileImageName, setMobileImageName] = useState('');
    const [removeMobileImage, setRemoveMobileImage] = useState(false);

    const mobileSize = typeOptions.find(typeOption => typeOption.value === type)?.mobileSize ?? null;

    const [bannerToDelete, setBannerToDelete] = useState(null);

    useEffect(() => {
        getFormats();
        getBanners();
    }, []);

    useEffect(() => {
        generateData();
    }, [banners, typeOptions]);

    useEffect(() => {
        if(!showCreate) clean();
    }, [showCreate]);

    useEffect(() => {
        if(!showEdit) clean();
    }, [showEdit]);

    // Os formatos são do tema do site que o tenant está usando; o backend
    // resolve isso e devolve junto com o tema.
    const getFormats = () => {
        api.get('/theme').then(res => {
            setTypeOptions(toTypeOptions(res.data.banner_formats));
        }).catch(error => {
            renderError(error);
        });
    }

    const getBanners = () => {
        api.get('/banner').then(res => {
            setBanners(res.data.resources);
        }).catch(error => {
            renderError(error);
        }).then(() => setLoading(false));
    }

    const generateData = () => {
        const header = ['Imagem', 'Nome', 'Status', 'Link'];
        const rows = {};

        typeOptions.map(type => {
            rows[type.value] = banners
                .filter(banner => banner.type === type.value)
                .sort((a, b) => a.position - b.position)
                .map(banner => ({
                    id: banner.id,
                    data: [
                        <img src={banner.image_url.small} />,
                        banner.name,
                        banner.status ? 'Ativo' : 'Inativo',
                        banner.link
                    ],
                    action: () => editBanner(banner)
                }));
        })

        setData({header, rows});
    }

    // Reordena os banners de um tipo por arrasta e solta. Atualiza otimista
    // (recalcula as posições em memória) e reverte se o backend falhar.
    const reorderBanners = orderedIds => {
        const previous = banners;

        const positionById = {};
        orderedIds.forEach((id, index) => { positionById[id] = index + 1; });

        setBanners(banners.map(banner => (
            positionById[banner.id] != null
                ? { ...banner, position: positionById[banner.id] }
                : banner
        )));

        api.put(`/banner/reorder`, { ids: orderedIds }).catch(error => {
            setBanners(previous);
            renderError(error);
        });
    }

    const storeBanner = () => {
        setLoading(true);

        // Novo banner vai para o fim da lista do seu tipo — a ordem fina é
        // ajustada depois por arrasta e solta.
        const typePositions = banners.filter(banner => banner.type === type).map(banner => banner.position);
        const nextPosition = typePositions.length ? Math.max(...typePositions) + 1 : 1;

        api.post(`/banner`, {
            name,
            status,
            position: nextPosition,
            type,
            link,
            target,
            image: imageBase64,
            mobile_image: mobileSize ? mobileImageBase64 : ''
        }).then(res => {
            getBanners();
            setShowCreate(false);
            toast.success('Banner cadastrado com sucesso');
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    }

    const editBanner = banner => {
        setId(banner.id);
        setName(banner.name);
        setStatus(banner.status);
        setPosition(banner.position);
        setType(banner.type);
        setLink(banner.link);
        setTarget(banner.target);
        setImageBase64('');
        setImageName(banner.image_url.small);
        setMobileImageBase64('');
        setMobileImageName(banner.mobile_image_url?.small ?? '');
        setRemoveMobileImage(false);
        setShowEdit(true);
    }

    const updateBanner = () => {
        setLoading(true);

        api.put(`/banner/${id}`, {
            name,
            status,
            position,
            type,
            link,
            target,
            image: imageBase64,
            mobile_image: mobileSize ? mobileImageBase64 : '',
            remove_mobile_image: removeMobileImage
        }).then(res => {
            getBanners();
            setShowEdit(false);
            toast.success('Banner atualizado com sucesso');
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    }

    const clean = () => {
        setId('');
        setName('');
        setStatus('');
        setPosition('');
        setType('');
        setLink('');
        setTarget('');
        setImageBase64('');
        setImageName('');
        setMobileImageBase64('');
        setMobileImageName('');
        setRemoveMobileImage(false);
    }

    const changeMobileImageName = value => {
        setMobileImageName(value);
        setRemoveMobileImage(false);
    }

    const clearMobileImage = event => {
        event.preventDefault();
        setMobileImageBase64('');
        setMobileImageName('');
        setRemoveMobileImage(true);
    }

    const deleteBanner = () => {
        setLoading(true);

        api.delete(`/banner/${bannerToDelete}`).then(res => {
            setBannerToDelete(null);
            setShowEdit(false);
            getBanners();
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    }

    return (
        <>
            {(showCreate || showEdit) &&
                <Modal
                    title={`${showCreate ? 'Cadastrar' : 'Editar'} banner`}
                    show={true}
                    close={() => showCreate ? setShowCreate(false) : setShowEdit(false)}
                    footer={
                        <>
                            {showEdit &&
                                <Button
                                    type={`secondary`}
                                    size={`small`}
                                    text={`Excluir`}
                                    svg={`delete-small`}
                                    action={() => setBannerToDelete(id)}
                                    loading={loading} 
                                />
                            }
                            <Button
                                type={`primary`}
                                size={`small`}
                                text={showCreate ? 'Cadastrar' : 'Salvar alterações'}
                                svg={`save-white`}
                                action={() => showCreate ? storeBanner() : updateBanner()}
                                loading={loading} 
                            />
                        </>
                    }
                >
                    <div className="section">
                        <div className="row">
                            <div className="col-6">
                                <InputSelect label={`Status`} options={statusOptions} value={status} change={setStatus} />
                            </div>
                            <div className="col-6">
                                <Input type={`text`} label={`Nome`} value={name} change={setName} />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-12">
                                <InputSelect label={`Tipo`} options={typeOptions} value={type} change={setType} />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-6">
                                <InputFile 
                                    label={mobileSize ? `Imagem para computador` : `Imagem`} 
                                    base64={imageBase64}
                                    changeBase64={setImageBase64}
                                    name={imageName}
                                    changeName={setImageName}
                                />
                            </div>
                            {mobileSize &&
                                <div className="col-6">
                                    <InputFile
                                        label={`Imagem para celular (opcional)`}
                                        base64={mobileImageBase64}
                                        changeBase64={setMobileImageBase64}
                                        name={mobileImageName}
                                        changeName={changeMobileImageName}
                                        description={
                                            <>
                                                {mobileSize}. Sem ela, o celular usa a do computador.
                                                {mobileImageName !== '' && <> <a href="#" onClick={clearMobileImage}>Remover</a></>}
                                            </>
                                        }
                                    />
                                </div>
                            }
                        </div>
                        <div className="row">
                            <div className="col-6">
                                <Input type={`text`} label={`Link`} value={link} change={setLink} />
                            </div>
                            <div className="col-6">
                                <InputSelect label={`Abertura em`} options={targetOptions} value={target} change={setTarget} />
                            </div>
                        </div>
                    </div>
                </Modal>
            }
            <ModalDelete
                show={bannerToDelete !== null}
                action={deleteBanner}
                close={() => setBannerToDelete(null)}
                loading={loading}
            />
            <PanelTemplate id="banner-list">
                <PageHeader
                    right={
                        <>
                            <Button type={`primary`} size={`small`} text={`Cadastrar banner`} svg="plus-white" action={() => setShowCreate(true)}  />
                            {/* <Button type={`secondary`} size={`small`} text={`Filtrar`} svg="filter"  /> */}
                        </>
                    }
                />
                <BoxTabs
                    titles={typeOptions.map(typeOption => typeOption.aba)}
                    contents={typeOptions.map(typeOption => (
                        <SortableTable
                            header={data.header}
                            rows={data.rows[typeOption.value]}
                            loading={loading}
                            onReorder={reorderBanners}
                        />
                    ))}
                />
            </PanelTemplate>
        </>
    );
}

export default BannerList;